import BaseController, { INVITE_VALUE_STATE_NONE, type InviteValueState } from "./BaseController";
import Fragment from "sap/ui/core/Fragment";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import type { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import type ListBinding from "sap/ui/model/ListBinding";
import type Table from "sap/m/Table";
import type List from "sap/m/List";
import type Dialog from "sap/m/Dialog";
import type Input from "sap/m/Input";
import type TextArea from "sap/m/TextArea";
import type CheckBox from "sap/m/CheckBox";
import type Select from "sap/m/Select";
import type { Switch$ChangeEvent } from "sap/m/Switch";
import type { ResponseError } from "../types/Errors";
import { getCountries, type CountryItem } from "../model/countries";
import { PERMISSION_CATALOG } from "../auth/PermissionCatalog";
import { problemMessage } from "../util/ProblemDetails";
import { showInviteOutcome, type InviteUserResponse } from "../util/InviteOutcome";

interface KpiCounts {
	pendingOrders: string;
	pendingOrdersState: "None" | "Success" | "Warning" | "Error" | "Information";
	activeLicenses: string;
	activeDeployments: string;
	activeContacts: string;
}

interface ProfileEditDraft {
	country: string;
	city: string;
	region: string;
	postalCode: string;
	email: string;
	contractDate: string;
	contractExpiration: string;
	customerStatus: string;
	cardCode: string;
	// ADR-013: domyślna Location dla Partner-org. "" gdy null lub gdy Org typu CompuTec/EndCustomer
	// (pole nie ma znaczenia poza Partnerem). Save handler wysyła osobnym PUT do
	// /default-location (różny permission OrgsEdit niż profile UpdateOrgProfile).
	defaultLocationId: string;
}

const CARD_CODE_PATTERN = /^[A-Za-z0-9]{1,50}$/;

interface ViewState {
	orgId: string;
	from: string;
	contactDialogMode: "add" | "edit";
	contactDialogTitle: string;
	editingContactId: string;
	isEndCustomer: boolean;
	isEditMode: boolean;
	editDraft: ProfileEditDraft;
	cardCodeValueState: "None" | "Error";
	countries: CountryItem[];
	parentId: string;
	parentName: string;
	users: PortalUserListItem[];
	// InviteUserDialog (shared fragment) — pre-fill na bieżącą organizację. Picker UKRYTY,
	// bo z kontekstu karty wiemy do której org-i invitować (kasuje to bug "_System_Internal"
	// pojawiający się w pickerze gdy fragment renderował go z visible=undefined→true).
	inviteShowOrgPicker: boolean;
	inviteOrgId: string;
	inviteOrgName: string;
	inviteCompuTecOrgId: string;
	inviteRoleIsUser: boolean;
	inviteHasPortalAccess: boolean;
	inviteValueState: InviteValueState;
	permissionRows: PermissionRow[];
	editingPermissionsUserId: string;
	kpi: KpiCounts;
}

const EMPTY_DRAFT: ProfileEditDraft = {
	country: "",
	city: "",
	region: "",
	postalCode: "",
	email: "",
	contractDate: "",
	contractExpiration: "",
	customerStatus: "",
	cardCode: "",
	defaultLocationId: ""
};

/**
 * Wiersz listy użytkowników portalu (REST: <c>GET /api/v1/organizations/{id}/users</c>).
 * Zgodny z <c>UserEndpoints.PortalUserListItem</c> w backendzie.
 */
interface PortalUserListItem {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	role: "User" | "Admin" | "Auditor" | "MarketingManager" | "Developer";
	profile: "ReadOnly" | "Standard" | "Full" | null;
	status: "Pending" | "Active" | "Disabled" | "Removed";
	permissionOverrides: string[];
}

/**
 * Wiersz w dialogu edycji permission overrides — jeden permission code z trzema stanami radio.
 * <list type="bullet">
 *   <item><c>inherit</c> — domyślnie (brak override'a w bazie)</item>
 *   <item><c>grant</c> — override <c>+code</c> (włącz mimo profilu)</item>
 *   <item><c>deny</c> — override <c>-code</c> (wyłącz mimo profilu)</item>
 * </list>
 */
interface PermissionRow {
	code: string;
	label: string;
	state: "inherit" | "grant" | "deny";
}

/**
 * Strona szczegółów pojedynczej organizacji (route: organization/{id}).
 *
 * Bindowanie:
 * - Cała view jest zbindowana do <c>odata>/Organizations(id)</c> przez bindElement w
 *   <see cref="onPatternMatched"/> — pola Country/City/.../CustomerStatus pochodzą z tej entity.
 * - Lista kontaktów (id <c>contactsTable</c>) i notatek (<c>notesList</c>) bindowana w XML do
 *   osobnych entity setów <c>ContactPersons</c> / <c>OrganizationNotes</c> z filtrem
 *   <c>OrganizationId eq {view>/orgId}</c>; <c>orgId</c> wstrzykujemy do view modelu po routing match.
 *
 * @namespace computec.b2b.portal.controller
 */
export default class OrganizationDetail extends BaseController {
	private addNoteDialog?: Dialog;
	private addContactDialog?: Dialog;
	private inviteUserDialog?: Dialog;
	private editUserPermissionsDialog?: Dialog;

	public override onInit(): void {
		const initial: ViewState = {
			orgId: "",
			from: "organizations",
			contactDialogMode: "add",
			contactDialogTitle: "",
			editingContactId: "",
			isEndCustomer: false,
			isEditMode: false,
			editDraft: { ...EMPTY_DRAFT },
			cardCodeValueState: "None",
			// Lista krajów raz na onInit — UI5 LocaleData jest sync (CLDR data preloaded
			// z ui5 distro), brak ryzyka racing w bindingu ComboBox.
			countries: getCountries(),
			parentId: "",
			parentName: "",
			users: [],
			inviteShowOrgPicker: false,
			inviteOrgId: "",
			inviteOrgName: "",
			inviteCompuTecOrgId: "",
			inviteRoleIsUser: true,
			inviteHasPortalAccess: true,
			inviteValueState: { ...INVITE_VALUE_STATE_NONE },
			permissionRows: [],
			editingPermissionsUserId: "",
			kpi: {
				pendingOrders: "—",
				pendingOrdersState: "None",
				activeLicenses: "—",
				activeDeployments: "—",
				activeContacts: "—"
			}
		};
		this.getView().setModel(new JSONModel(initial), "view");
		// ADR-025 — payment activation policy. Ładowana z OData po routing match przez loadPaymentPolicy.
		this.getView().setModel(new JSONModel({ policy: "RequirePaymentBeforeActivation", graceDays: 14, dirty: false, editing: false }), "payPolicy");
		const attach = (routeName: string, from: string) => {
			this.getRouter().getRoute(routeName)?.attachPatternMatched((e) => {
				this.onPatternMatched(e, from);
			});
		};
		attach("organizationDetail", "organizations");
		attach("partnerDetail", "partners");
		attach("customerDetail", "customers");
		// My company: a top-level page with no list behind it, so no back target.
		attach("myCompany", "");
	}

	private onPatternMatched(event: Route$PatternMatchedEvent, from = "organizations"): void {
		const args = event.getParameter("arguments") as { id?: string } | undefined;
		const userModel = this.getOwnerComponent().getModel("user") as JSONModel | undefined;
		const id = args?.id ?? (userModel?.getProperty("/orgId") as string | undefined) ?? "";
		if (!id) {
			this.getRouter().navTo("dashboard", {}, undefined, true);
			return;
		}

		const view = this.getView().getModel("view") as JSONModel;
		view.setProperty("/orgId", id);
		view.setProperty("/from", from);
		view.setProperty("/parentId", "");
		view.setProperty("/parentName", "");
		// Nawigacja do innej organizacji wychodzi z trybu edycji bez zapisu — niezapisany
		// draft zostaje porzucony intencjonalnie (route change ≡ Cancel).
		view.setProperty("/isEditMode", false);
		view.setProperty("/editDraft", { ...EMPTY_DRAFT });

		// bindElement: pojedyncza encja po Id; OData v4 zaakceptuje Guid jako literal w nawiasach
		// pod warunkiem zacytowania (Organizations(<guid>) bez cudzysłowów dla typu Guid).
		this.getView().bindElement({
			path: `/Organizations(${id})`,
			model: "odata"
		});

		// Filtry sub-list (kontakty, notatki) — w XML nie da się zbindować value1 do property
		// view modelu (UI5 traktuje '{view>/orgId}' jako literal string), więc ustawiamy je tu
		// po znaniu id z route.
		const filter = new Filter("OrganizationId", FilterOperator.EQ, id);
		((this.byId("contactsTable") as Table | undefined)?.getBinding("items") as ListBinding | undefined)?.filter(filter);
		((this.byId("notesList") as List | undefined)?.getBinding("items") as ListBinding | undefined)?.filter(filter);

		// Parent organization (CompuTec / Partner) — robimy 2 osobne fetch zamiast $expand,
		// bo nie mamy navigation property w EDM. Pierwszy zwraca ParentOrganizationId, drugi nazwę
		// rodzica. Niewidoczne dla CompuTec root (ParentOrganizationId = null).
		void this.loadParentName(id);

		// Lista PortalUser'ów — z REST endpointu (nie ma OData entity setu Users w I8).
		void this.loadUsers(id);

		// 4 indykatory pod nagłówkiem (Pending Orders / Active Licenses / Active Deployments
		// / Active Contacts). Każdy to osobny $count z filterem po Id organizacji.
		void this.loadKpis(id);

		// ADR-025 — polityka aktywacji płatności (osobny REST/OData fetch, nie ma jej w bindElement $select).
		void this.loadPaymentPolicy(id);
	}

	/**
	 * ADR-025 — ładuje politykę aktywacji płatności do modelu <c>payPolicy</c>. OData feed
	 * eksponuje <c>PaymentActivationPolicy</c> + <c>DefaultPaymentGraceDays</c> (Task 13).
	 */
	private async loadPaymentPolicy(orgId: string): Promise<void> {
		const m = this.getView().getModel("payPolicy") as JSONModel;
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const data = await fetcher.json<{ PaymentActivationPolicy: string; DefaultPaymentGraceDays: number }>(
				`/odata/v4/Organizations(${orgId})?$select=PaymentActivationPolicy,DefaultPaymentGraceDays`
			);
			m.setProperty("/policy", data.PaymentActivationPolicy);
			m.setProperty("/graceDays", data.DefaultPaymentGraceDays > 0 ? data.DefaultPaymentGraceDays : 14);
			m.setProperty("/dirty", false);
			m.setProperty("/editing", false);
		} catch (e) {
			m.setProperty("/dirty", false);
			m.setProperty("/editing", false);
			console.warn("OrganizationDetail.loadPaymentPolicy failed:", e);
		}
	}

	private async loadKpis(orgId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;

		const queries: Array<{ path: string; entitySet: string; filter: string }> = [
			{ path: "/kpi/pendingOrders",     entitySet: "Orders",         filter: `BeneficiaryOrgId eq ${orgId} and Status eq 'Placed'` },
			{ path: "/kpi/activeLicenses",    entitySet: "Licenses",       filter: `OwnerOrgId eq ${orgId} and Status eq 'Active'` },
			{ path: "/kpi/activeDeployments", entitySet: "Deployments",    filter: `OwnerOrgId eq ${orgId} and Status eq 'Active'` },
			{ path: "/kpi/activeContacts",    entitySet: "ContactPersons", filter: `OrganizationId eq ${orgId} and IsActive eq true` }
		];

		await Promise.all(queries.map(async q => {
			const value = await this.fetchCount(q.entitySet, q.filter);
			view.setProperty(q.path, value === null ? "—" : String(value));
		}));

		// Pending orders state — Critical jeśli > 0, None gdy 0/—.
		const pending = view.getProperty("/kpi/pendingOrders") as string;
		const pendingState = (pending !== "—" && Number(pending) > 0) ? "Warning" : "None";
		view.setProperty("/kpi/pendingOrdersState", pendingState);
	}

	private async fetchCount(entitySet: string, filter: string): Promise<number | null> {
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const url = `/odata/v4/${entitySet}/$count?$filter=${encodeURIComponent(filter)}`;
			const r = await fetcher.fetch(url, { headers: { Accept: "text/plain" } });
			if (!r.ok) {
				return null;
			}
			const n = Number((await r.text()).trim());
			return Number.isFinite(n) ? n : null;
		} catch {
			return null;
		}
	}

	private async loadUsers(orgId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const users = await fetcher.json<PortalUserListItem[]>(
				`/api/v1/organizations/${orgId}/users`
			);
			view.setProperty("/users", users);
		} catch (e) {
			// Brak permissionu (portal-users.read) → 403; po prostu pokazujemy pustą listę.
			// Dla innych błędów logujemy w konsoli, nie blokujemy widoku.
			view.setProperty("/users", []);
			console.warn("OrganizationDetail.loadUsers failed:", e);
		}
	}

	private async loadParentName(orgId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const org = await fetcher.json<{ ParentOrganizationId: string | null }>(
				`/odata/v4/Organizations(${orgId})?$select=ParentOrganizationId`
			);
			if (!org.ParentOrganizationId) {
				return;
			}
			const parent = await fetcher.json<{ Name: string }>(
				`/odata/v4/Organizations(${org.ParentOrganizationId})?$select=Name`
			);
			view.setProperty("/parentId", org.ParentOrganizationId);
			view.setProperty("/parentName", parent.Name);
		} catch {
			// Jeśli rodzic jest poza tenant scope (np. CompuTec dla partnera) → 404 z OData,
			// nie pokazujemy linka. Cicho ignorujemy — to legalna sytuacja, nie błąd.
		}
	}

	public onOpenParent(): void {
		const parentId = (this.getView().getModel("view") as JSONModel).getProperty("/parentId") as string;
		if (!parentId) {
			return;
		}
		this.navToOrganization(parentId);
	}

	public onRefresh(): void {
		this.getView().getModel("odata")?.refresh();
	}

	/**
	 * Skrót "Place order" — przeskakuje na stronę nowego zamówienia z prefillowanym beneficjentem
	 * (route <c>orderNewFor</c>). OrderNew sam dociągnie szczegóły orga i ustawi tryb wystawcy.
	 */
	public onPlaceOrder(): void {
		const view = this.getView().getModel("view") as JSONModel;
		const orgId = view.getProperty("/orgId") as string;
		if (!orgId) {
			return;
		}
		this.navTo("orderNewFor", { beneficiaryId: orgId });
	}

	public override onNavBack(): void {
		const from = (this.getView().getModel("view") as JSONModel).getProperty("/from") as string | undefined;
		if (from === "") {
			return;
		}
		this.navTo(from ?? "organizations");
	}

	public async onAddContact(): Promise<void> {
		await this.openContactDialog("add", null);
	}

	public async onEditContact(event: { getSource(): { getBindingContext(name: string): { getProperty(name: string): unknown } | null } }): Promise<void> {
		const ctx = event.getSource().getBindingContext("odata");
		if (!ctx) {
			return;
		}
		await this.openContactDialog("edit", {
			id: ctx.getProperty("Id") as string,
			firstName: ctx.getProperty("FirstName") as string,
			lastName: ctx.getProperty("LastName") as string,
			email: (ctx.getProperty("Email") as string | null) ?? "",
			phone: (ctx.getProperty("Phone") as string | null) ?? "",
			position: (ctx.getProperty("Position") as string | null) ?? "",
			isActive: ctx.getProperty("IsActive") as boolean
		});
	}

	private async openContactDialog(
		mode: "add" | "edit",
		contact: { id: string; firstName: string; lastName: string; email: string; phone: string; position: string; isActive: boolean } | null
	): Promise<void> {
		const bundle = await this.getResourceBundle();
		const view = this.getView().getModel("view") as JSONModel;
		view.setProperty("/contactDialogMode", mode);
		view.setProperty("/editingContactId", contact?.id ?? "");
		view.setProperty(
			"/contactDialogTitle",
			mode === "edit"
				? (bundle.getText("organization.detail.contacts.dialog.editTitle") ?? "Edytuj kontakt")
				: (bundle.getText("organization.detail.contacts.dialog.title") ?? "Dodaj kontakt")
		);

		if (!this.addContactDialog) {
			this.addContactDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.AddContactDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.addContactDialog);
		}

		// Wypełnienie/reset pól — Fragment.load cache'uje instancję, więc poprzednie wartości
		// muszą zostać nadpisane przy każdym otwarciu (zarówno add → reset, edit → preload).
		(this.byId("inpContactFirstName") as Input).setValue(contact?.firstName ?? "");
		(this.byId("inpContactLastName") as Input).setValue(contact?.lastName ?? "");
		const emailField = this.byId("inpContactEmail") as Input;
		emailField.setValue(contact?.email ?? "");
		emailField.setValueState("None");
		(this.byId("inpContactPhone") as Input).setValue(contact?.phone ?? "");
		(this.byId("inpContactPosition") as Input).setValue(contact?.position ?? "");
		(this.byId("cbContactActive") as CheckBox).setSelected(contact?.isActive ?? true);

		this.addContactDialog.open();
	}

	public onCancelContact(): void {
		this.addContactDialog?.close();
	}

	public onDeleteContact(event: { getSource(): { getBindingContext(name: string): { getProperty(name: string): unknown } | null } }): void {
		const ctx = event.getSource().getBindingContext("odata");
		if (!ctx) {
			return;
		}
		const contactId = ctx.getProperty("Id") as string;
		const name = `${(ctx.getProperty("FirstName") as string | null) ?? ""} ${(ctx.getProperty("LastName") as string | null) ?? ""}`.trim();
		void this.confirmDeleteContact(contactId, name);
	}

	private async confirmDeleteContact(contactId: string, name: string): Promise<void> {
		const bundle = await this.getResourceBundle();
		const orgId = (this.getView().getModel("view") as JSONModel).getProperty("/orgId") as string;
		// MessageBox.Action wartości runtime to stringi — lokalnie traktujemy jako string (jak niżej).
		const okAction = MessageBox.Action.OK as unknown as string;
		MessageBox.confirm(
			bundle.getText("organization.detail.contacts.delete.confirm", [name]) ?? `Usunąć kontakt ${name}?`,
			{
				title: bundle.getText("organization.detail.contacts.delete.title") ?? "Usuń kontakt",
				onClose: (oAction: string) => {
					if (oAction === okAction) {
						void this.deleteContact(orgId, contactId);
					}
				}
			}
		);
	}

	private async deleteContact(orgId: string, contactId: string): Promise<void> {
		const bundle = await this.getResourceBundle();
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/organizations/${orgId}/contacts/${contactId}`, { method: "DELETE" });
			MessageToast.show(bundle.getText("organization.detail.contacts.delete.success") ?? "Usunięto kontakt");
			this.getView().getModel("odata")?.refresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("organization.detail.contacts.delete.failure") ?? "Nie udało się usunąć kontaktu."));
		}
	}

	public async onSaveContact(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const view = this.getView().getModel("view") as JSONModel;
		const orgId = view.getProperty("/orgId") as string;
		const mode = view.getProperty("/contactDialogMode") as "add" | "edit";
		const contactId = view.getProperty("/editingContactId") as string;

		const firstName = (this.byId("inpContactFirstName") as Input).getValue().trim();
		const lastName = (this.byId("inpContactLastName") as Input).getValue().trim();
		const email = (this.byId("inpContactEmail") as Input).getValue().trim();
		const phone = (this.byId("inpContactPhone") as Input).getValue().trim();
		const position = (this.byId("inpContactPosition") as Input).getValue().trim();
		const isActive = (this.byId("cbContactActive") as CheckBox).getSelected();

		const emailInput = this.byId("inpContactEmail") as Input;
		emailInput.setValueState("None");

		if (firstName.length === 0 || lastName.length === 0) {
			MessageBox.warning(bundle.getText("organization.detail.contacts.dialog.error.namesRequired") ?? "Imię i nazwisko są wymagane.");
			return;
		}
		if (email.length === 0) {
			emailInput.setValueState("Error");
			MessageBox.warning(bundle.getText("organization.detail.contacts.dialog.error.emailRequired") ?? "A valid email is required.");
			return;
		}
		// Wymagamy pełnego kształtu local@domain.tld (min. 2-znakowy TLD po kropce) — samo "@"
		// przepuszczało adresy bez końcówki domeny (np. "a@b"). Backend waliduje ostatecznie.
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
			emailInput.setValueState("Error");
			MessageBox.warning(bundle.getText("organization.detail.contacts.dialog.error.emailInvalid") ?? "Podaj poprawny adres e-mail (np. jan@firma.pl).");
			return;
		}

		const basePayload = {
			firstName,
			lastName,
			email,
			phone: phone.length > 0 ? phone : null,
			position: position.length > 0 ? position : null
		};

		const url = mode === "edit"
			? `/api/v1/organizations/${orgId}/contacts/${contactId}`
			: `/api/v1/organizations/${orgId}/contacts`;
		const httpMethod = mode === "edit" ? "PUT" : "POST";
		const payload = mode === "edit" ? { ...basePayload, isActive } : basePayload;
		const successKey = mode === "edit"
			? "organization.detail.contacts.dialog.successUpdate"
			: "organization.detail.contacts.dialog.success";
		const failureKey = mode === "edit"
			? "organization.detail.contacts.dialog.failureUpdate"
			: "organization.detail.contacts.dialog.failure";

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(url, {
				method: httpMethod,
				body: JSON.stringify(payload)
			});
			MessageToast.show(bundle.getText(successKey) ?? "OK");
			this.addContactDialog?.close();
			// Refresh całego modelu OData zamiast samego list bindingu — w OData v4 cache jest
			// key-based, a listy z filterem ignorują podmiany pojedynczych pól wierszy do
			// momentu pełnego refetch'a. `getBinding("items").refresh()` na takiej liście
			// nie aktualizuje IsActive bez re-fetcha całego entity setu.
			this.getView().getModel("odata")?.refresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText(failureKey) ?? "Operacja nie powiodła się."));
		}
	}

	public async onAddNote(): Promise<void> {
		if (!this.addNoteDialog) {
			this.addNoteDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.AddNoteDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.addNoteDialog);
		}
		// Reset pól przy każdym otwarciu — Fragment.load zwraca cached instance.
		(this.byId("inpNoteBody") as TextArea).setValue("");
		(this.byId("cbNoteVisibleToPartner") as CheckBox).setSelected(true);
		this.addNoteDialog.open();
	}

	public onCancelNote(): void {
		this.addNoteDialog?.close();
	}

	public async onSaveNote(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const orgId = (this.getView().getModel("view") as JSONModel).getProperty("/orgId") as string;
		const body = (this.byId("inpNoteBody") as TextArea).getValue().trim();
		const isVisibleToPartner = (this.byId("cbNoteVisibleToPartner") as CheckBox).getSelected();

		if (body.length === 0) {
			MessageBox.warning(bundle.getText("organization.detail.notes.dialog.error.bodyRequired") ?? "Treść notatki jest wymagana.");
			return;
		}

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/organizations/${orgId}/notes`, {
				method: "POST",
				body: JSON.stringify({ body, isVisibleToPartner })
			});
			MessageToast.show(bundle.getText("organization.detail.notes.dialog.success") ?? "Notatka dodana.");
			this.addNoteDialog?.close();
			// Wymusza ponowny fetch listy notatek z OData (RP-side filter już jest ustawiony).
			((this.byId("notesList") as List | undefined)?.getBinding("items") as ListBinding | undefined)?.refresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("organization.detail.notes.dialog.failure") ?? "Nie udało się dodać notatki."));
		}
	}

	/**
	 * Przejście w tryb edycji profilu organizacji (sekcje Address + Contract). Zamiast dialogu
	 * (poprzednia implementacja) pola tych sub-sekcji rerenderują się jako Input/DatePicker/Select
	 * zbindowane do `view>/editDraft`. Akcje w headerze przełączają się na Save/Cancel.
	 */
	public onEnterEditMode(): void {
		const orgCtx = this.getView().getBindingContext("odata");
		if (!orgCtx) {
			return;
		}
		const view = this.getView().getModel("view") as JSONModel;
		const isEndCustomer = orgCtx.getProperty("Type") === "EndCustomer";
		view.setProperty("/isEndCustomer", isEndCustomer);
		view.setProperty("/editDraft", {
			country: (orgCtx.getProperty("Country") as string | null) ?? "",
			city: (orgCtx.getProperty("City") as string | null) ?? "",
			region: (orgCtx.getProperty("Region") as string | null) ?? "",
			postalCode: (orgCtx.getProperty("PostalCode") as string | null) ?? "",
			email: (orgCtx.getProperty("Email") as string | null) ?? "",
			contractDate: (orgCtx.getProperty("ContractDate") as string | null) ?? "",
			contractExpiration: (orgCtx.getProperty("ContractExpirationDate") as string | null) ?? "",
			customerStatus: (orgCtx.getProperty("CustomerStatus") as string | null) ?? "",
			cardCode: (orgCtx.getProperty("CardCode") as string | null) ?? "",
			defaultLocationId: (orgCtx.getProperty("DefaultLocationId") as string | null) ?? ""
		} satisfies ProfileEditDraft);
		view.setProperty("/cardCodeValueState", "None");
		view.setProperty("/isEditMode", true);
	}

	/**
	 * Live walidacja CardCode podczas wpisywania — daje natychmiastowy feedback (czerwona ramka)
	 * zanim user kliknie Zapisz. Pusty string = brak warninga (CardCode jest opcjonalny).
	 */
	public onCardCodeLiveChange(): void {
		const view = this.getView().getModel("view") as JSONModel;
		const value = (view.getProperty("/editDraft/cardCode") as string).trim();
		const ok = value.length === 0 || CARD_CODE_PATTERN.test(value);
		view.setProperty("/cardCodeValueState", ok ? "None" : "Error");
	}

	public onCancelEdit(): void {
		const view = this.getView().getModel("view") as JSONModel;
		view.setProperty("/isEditMode", false);
		view.setProperty("/editDraft", { ...EMPTY_DRAFT });
		view.setProperty("/cardCodeValueState", "None");
	}

	public async onSaveProfile(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const view = this.getView().getModel("view") as JSONModel;
		const orgId = view.getProperty("/orgId") as string;
		const draft = view.getProperty("/editDraft") as ProfileEditDraft;

		const country = draft.country.trim();
		const city = draft.city.trim();
		const region = draft.region.trim();
		const postalCode = draft.postalCode.trim();
		const email = draft.email.trim();
		const contractDate = draft.contractDate;
		const contractExpiration = draft.contractExpiration;
		const customerStatusKey = draft.customerStatus;
		const cardCode = draft.cardCode.trim();

		// Walidacja po stronie klienta — backend i tak ją powtórzy, ale early return chroni przed
		// niepotrzebnym round-trip i odpalaniem mutacji profilu zanim CardCode zostanie poprawiony.
		if (cardCode.length > 0 && !CARD_CODE_PATTERN.test(cardCode)) {
			view.setProperty("/cardCodeValueState", "Error");
			MessageBox.warning(bundle.getText("organization.detail.cardCode.invalidFormat") ?? "CardCode musi być alfanumeryczny (A-Z, 0-9), max 50 znaków.");
			return;
		}

		const profilePayload = {
			country: country.length > 0 ? country : null,
			city: city.length > 0 ? city : null,
			region: region.length > 0 ? region : null,
			postalCode: postalCode.length > 0 ? postalCode : null,
			email: email.length > 0 ? email : null,
			contractDate: contractDate.length > 0 ? contractDate : null,
			contractExpirationDate: contractExpiration.length > 0 ? contractExpiration : null,
			customerStatus: customerStatusKey.length > 0 ? customerStatusKey : null
		};

		const permissions = this.getOwnerComponent().getPermissions();
		const canEditIntegration = permissions.has("orgs.edit-integration");

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/organizations/${orgId}/profile`, {
				method: "PUT",
				body: JSON.stringify(profilePayload)
			});

			// Sekcja Integracja jest niezależna od profilu — wymaga osobnego permu
			// `orgs.edit-integration`. Jeśli user nie ma perma, sekcja w ogóle się nie
			// renderuje (visible w XML), więc draft.cardCode zostaje pusty/niezmieniony.
			// Wywołanie /integration robimy tylko gdy user ma uprawnienia.
			if (canEditIntegration) {
				await fetcher.json(`/api/v1/organizations/${orgId}/integration`, {
					method: "PUT",
					body: JSON.stringify({ cardCode: cardCode.length > 0 ? cardCode : null })
				});
			}

			// ADR-013: DefaultLocation osobnym endpointem (różna permission egzekwowana — tu OrgsEdit
			// jak profile, więc każdy user mający orgs.edit może też ustawić Default Location).
			// Pole sensowne tylko dla Partner-org — sekcja w XML jest visible po Type=Partner,
			// dla CompuTec/EndCustomer pole jest puste i pomijamy wywołanie.
			const orgType = this.getView().getBindingContext("odata")?.getProperty("Type") as string | undefined;
			if (orgType === "Partner") {
				const currentDefaultLoc = (this.getView().getBindingContext("odata")?.getProperty("DefaultLocationId") as string | null) ?? "";
				const draftDefaultLoc = draft.defaultLocationId.trim();
				if (currentDefaultLoc !== draftDefaultLoc) {
					await fetcher.json(`/api/v1/organizations/${orgId}/default-location`, {
						method: "PUT",
						body: JSON.stringify({ locationId: draftDefaultLoc.length > 0 ? draftDefaultLoc : null })
					});
				}
			}

			MessageToast.show(bundle.getText("organization.detail.profile.dialog.success") ?? "Zapisano profil.");
			view.setProperty("/isEditMode", false);
			view.setProperty("/editDraft", { ...EMPTY_DRAFT });
			view.setProperty("/cardCodeValueState", "None");
			// Refresh całej organizacji — bindElement to context binding, refreshujemy model.
			this.getView().getModel("odata")?.refresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("organization.detail.profile.dialog.failure") ?? "Nie udało się zapisać profilu."));
		}
	}

	/**
	 * P2 — toggle Organization.AutoExtendDemo. Endpoint:
	 * <c>PATCH /api/v1/organizations/{id}/auto-extend-demo</c> z body <c>{ "Enabled": bool }</c>.
	 * Permission <c>orgs.edit_auto_extend_demo</c> (CompuTec-only). Switch jest bindowany do
	 * <c>odata>AutoExtendDemo</c> bezpośrednio — przy błędzie revertujemy state ręcznie
	 * (OData v4 cache nie odświeży się sam dla REST endpointu, więc po sukcesie też wymuszamy refresh).
	 */
	public async onAutoExtendDemoChanged(event: Switch$ChangeEvent): Promise<void> {
		const sw = event.getSource();
		const enabled = sw.getState();
		const view = this.getView().getModel("view") as JSONModel;
		const orgId = view.getProperty("/orgId") as string;
		if (!orgId) {
			return;
		}

		const bundle = await this.getResourceBundle();
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/organizations/${orgId}/auto-extend-demo`, {
				method: "PATCH",
				body: JSON.stringify({ enabled })
			});
			MessageToast.show(bundle.getText("organization.autoExtendDemo.savedToast") ?? "Updated");
			// Refresh OData entity — backend zaktualizował AutoExtendDemo, ale binding czyta
			// z cache OData v4 (read-only feed). Refresh wymuszą rebuilding bindingu z fresh data.
			this.getView().getModel("odata")?.refresh();
		} catch (e) {
			// Rollback wizualny — przywracamy poprzedni state Switcha, żeby UI nie wprowadzał
			// usera w błąd (zostało zapisane vs nie zostało).
			sw.setState(!enabled);
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("organization.autoExtendDemo.errorToast") ?? "Failed"));
		}
	}

	/**
	 * ADR-025 — oznacza politykę płatności jako zmienioną (aktywuje przycisk Save).
	 * Woływane z Select (change) oraz StepInput (change).
	 */
	public onPaymentPolicyFieldChange(): void {
		(this.getView().getModel("payPolicy") as JSONModel).setProperty("/dirty", true);
	}

	/**
	 * ADR-025 — pojedynczy przycisk polityki płatności działa jako toggle:
	 * w trybie odczytu ("Zmień") odblokowuje pola do edycji, w trybie edycji ("Zapisz")
	 * zapisuje zmiany. Po zapisie pola wracają do stanu zablokowanego.
	 */
	public onPaymentPolicyPrimary(): void {
		const m = this.getView().getModel("payPolicy") as JSONModel;
		if (!(m.getProperty("/editing") as boolean)) {
			m.setProperty("/editing", true);
			return;
		}
		void this.onSavePaymentPolicy();
	}

	/**
	 * ADR-025 — zapisuje politykę aktywacji płatności. Endpoint:
	 * <c>PATCH /api/v1/organizations/{id}/payment-policy</c> z body
	 * <c>{ policy, defaultGraceDays }</c> (enum jako string, camelCase). GraceDays wysyłamy
	 * tylko dla ActivateWithGracePeriod (dla pozostałych 0). Permission <c>orgs.edit_payment_policy</c>.
	 */
	public async onSavePaymentPolicy(): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const orgId = view.getProperty("/orgId") as string;
		if (!orgId) {
			return;
		}

		const m = this.getView().getModel("payPolicy") as JSONModel;
		const policy = m.getProperty("/policy") as string;
		const graceDays = policy === "ActivateWithGracePeriod" ? Number(m.getProperty("/graceDays")) : 0;
		const bundle = await this.getResourceBundle();
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/organizations/${orgId}/payment-policy`, {
				method: "PATCH",
				body: JSON.stringify({ policy, defaultGraceDays: graceDays })
			});
			m.setProperty("/dirty", false);
			m.setProperty("/editing", false);
			MessageToast.show(bundle.getText("organization.paymentPolicy.savedToast") ?? "Updated");
			this.getView().getModel("odata")?.refresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("organization.paymentPolicy.errorToast") ?? "Failed"));
		}
	}

	// === Users (zaproszenia + lifecycle) ===

	public async onInviteUser(): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const orgId = view.getProperty("/orgId") as string;

		if (!this.inviteUserDialog) {
			this.inviteUserDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.InviteUserDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.inviteUserDialog);
		}

		// Reset pól — Fragment.load zwraca cached instance; każde otwarcie startuje od pustego.
		// Org picker UKRYTY i pre-filled na bieżącą organizację karty (Partner / EndCustomer / CompuTec)
		// — wiemy z routy do której org-i invitujemy, bez sensu pokazywać picker.
		(this.byId("inpInviteEmail") as Input).setValue("");
		(this.byId("inpInviteFirstName") as Input).setValue("");
		(this.byId("inpInviteLastName") as Input).setValue("");
		(this.byId("ddInviteRole") as Select).setSelectedKey("User");
		view.setProperty("/inviteShowOrgPicker", false);
		view.setProperty("/inviteOrgId", orgId);
		const orgContext = this.getView().getBindingContext("odata");
		view.setProperty("/inviteOrgName", (orgContext?.getProperty("Name") as string | undefined) ?? "");
		// Card 31: the staff hint shows when this card's organization is CompuTec.
		const orgType = orgContext?.getProperty("Type") as string | undefined;
		view.setProperty("/inviteCompuTecOrgId", orgType === "CompuTec" ? orgId : "");
		view.setProperty("/inviteRoleIsUser", true);
		view.setProperty("/inviteHasPortalAccess", true);
		view.setProperty("/inviteValueState", { ...INVITE_VALUE_STATE_NONE });

		this.inviteUserDialog.open();
	}

	public onCancelInviteUser(): void {
		this.inviteUserDialog?.close();
	}

	public onInviteRoleChange(): void {
		const view = this.getView().getModel("view") as JSONModel;
		const role = (this.byId("ddInviteRole") as Select).getSelectedKey();
		view.setProperty("/inviteRoleIsUser", role === "User");
	}

	public onInvitePortalAccessChange(): void {
		// State zaktualizowany przez two-way binding na Switch.state. Nic więcej nie potrzebujemy —
		// fragment.xml ma `visible="{view>/inviteHasPortalAccess}"` na Role/Profile.
	}

	public async onSaveInviteUser(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const view = this.getView().getModel("view") as JSONModel;
		const orgId = view.getProperty("/orgId") as string;

		const email = (this.byId("inpInviteEmail") as Input).getValue().trim();
		const firstName = (this.byId("inpInviteFirstName") as Input).getValue().trim();
		const lastName = (this.byId("inpInviteLastName") as Input).getValue().trim();
		const hasPortalAccess = view.getProperty("/inviteHasPortalAccess") as boolean;
		const role = (this.byId("ddInviteRole") as Select).getSelectedKey() as "User" | "Admin" | "Auditor" | "MarketingManager" | "Developer";

		if (email.length === 0 || firstName.length === 0 || lastName.length === 0) {
			MessageBox.warning(bundle.getText("organization.detail.users.dialog.error.fieldsRequired") ?? "Wypełnij wszystkie wymagane pola.");
			return;
		}

		// ADR-014: HasPortalAccess=false → license-only user (Role MUST be null).
		const payload = {
			email,
			firstName,
			lastName,
			hasPortalAccess,
			role: hasPortalAccess ? role : null
		};

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const response = await fetcher.json<InviteUserResponse>(
				`/api/v1/organizations/${orgId}/users/invite`,
				{ method: "POST", body: JSON.stringify(payload) }
			);
			this.inviteUserDialog?.close();
			showInviteOutcome(bundle, response);

			await this.loadUsers(orgId);
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("organization.detail.users.dialog.failure") ?? "Nie udało się zaprosić użytkownika."));
		}
	}

	public async onDeactivateUser(event: { getSource(): { getBindingContext(name: string): { getProperty(name: string): unknown } | null } }): Promise<void> {
		const ctx = event.getSource().getBindingContext("view");
		if (!ctx) {
			return;
		}
		const userId = ctx.getProperty("id") as string;
		const userEmail = ctx.getProperty("email") as string;
		await this.confirmAndChangeUserStatus(
			userId,
			userEmail,
			"deactivate",
			"organization.detail.users.deactivate.confirm",
			"organization.detail.users.deactivate.success",
			"organization.detail.users.deactivate.failure"
		);
	}

	public async onReactivateUser(event: { getSource(): { getBindingContext(name: string): { getProperty(name: string): unknown } | null } }): Promise<void> {
		const ctx = event.getSource().getBindingContext("view");
		if (!ctx) {
			return;
		}
		const userId = ctx.getProperty("id") as string;
		const userEmail = ctx.getProperty("email") as string;
		await this.confirmAndChangeUserStatus(
			userId,
			userEmail,
			"reactivate",
			"organization.detail.users.reactivate.confirm",
			"organization.detail.users.reactivate.success",
			"organization.detail.users.reactivate.failure"
		);
	}

	private async confirmAndChangeUserStatus(
		userId: string,
		userEmail: string,
		action: "deactivate" | "reactivate",
		confirmKey: string,
		successKey: string,
		failureKey: string
	): Promise<void> {
		const bundle = await this.getResourceBundle();
		const orgId = (this.getView().getModel("view") as JSONModel).getProperty("/orgId") as string;
		const confirmText = (bundle.getText(confirmKey) ?? "Czy na pewno?") + `\n\n${userEmail}`;

		// MessageBox.Action wartości runtime to stringi ("OK", "CANCEL", ...). UI5 TS typing
		// nie eksportuje Action jako namespace — lokalnie traktujemy jako string.
		const okAction = MessageBox.Action.OK as unknown as string;
		MessageBox.confirm(confirmText, {
			actions: [MessageBox.Action.OK, MessageBox.Action.CANCEL],
			emphasizedAction: MessageBox.Action.OK,
			onClose: (oAction: string) => {
				if (oAction !== okAction) {
					return;
				}
				void this.callUserStatusEndpoint(orgId, userId, action, bundle.getText(successKey), bundle.getText(failureKey));
			}
		});
	}

	private async callUserStatusEndpoint(
		orgId: string,
		userId: string,
		action: "deactivate" | "reactivate",
		successText: string | undefined,
		failureText: string | undefined
	): Promise<void> {
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			// json() rzuca przy non-OK status (m.in. KeycloakProvisioningFailed → 422),
			// obsługuje 204 NoContent (handler zwraca Result.Success() bez body).
			await fetcher.json(`/api/v1/users/${userId}/${action}`, { method: "POST" });
			MessageToast.show(successText ?? "OK");
			await this.loadUsers(orgId);
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, failureText ?? "Operacja nie powiodła się."));
		}
	}

	// === Formattery dla tabeli userów ===
	// i18n nie jest dostępne w formatterze bez dodatkowego DI — zostawiamy stałe stringi
	// jako fallback, a właściwe lokalizowane teksty czytamy z bundle przez expressionkę nie
	// dałoby się — formatter musi być statyczny. UI5 best practice: formatter zwraca raw,
	// a kolumna ma tooltip/text z i18n bundle. Tutaj zwracamy stałe identyfikatory enum;
	// dla prostszego doświadczenia dev pokazujemy je 1:1 (i18n jest na nagłówkach kolumn
	// + na akcjach + statusach przez stan). W I9 (Audit viewer) prawdopodobnie wprowadzimy
	// helper formatterow z dostępem do bundle przez ResourceBundle event chain.

	public formatUserRole(role: string | null | undefined): string {
		return role ?? "";
	}

	public formatUserProfile(profile: string | null | undefined): string {
		return profile ?? "";
	}

	// === Permission overrides dialog ===

	/**
	 * Otwiera dialog edycji overrideów dla user'a z wiersza. Buduje listę <c>permissionRows</c>
	 * z pełnego katalogu + bieżącego stanu overrideów na user'ze (mapuje <c>+code</c> na
	 * <c>grant</c>, <c>-code</c> na <c>deny</c>, brak na <c>inherit</c>).
	 */
	public async onEditUserPermissions(event: { getSource(): { getBindingContext(name: string): { getProperty(name: string): unknown } | null } }): Promise<void> {
		const ctx = event.getSource().getBindingContext("view");
		if (!ctx) {
			return;
		}
		const userId = ctx.getProperty("id") as string;
		const existingOverrides = (ctx.getProperty("permissionOverrides") as string[] | null) ?? [];

		const view = this.getView().getModel("view") as JSONModel;
		view.setProperty("/editingPermissionsUserId", userId);
		const bundle = await this.getResourceBundle();
		view.setProperty("/permissionRows", PERMISSION_CATALOG.map((code): PermissionRow => {
			const label = bundle.getText(`permission.${code}`) || code;
			if (existingOverrides.includes(`+${code}`)) {
				return { code, label, state: "grant" };
			}
			if (existingOverrides.includes(`-${code}`)) {
				return { code, label, state: "deny" };
			}
			return { code, label, state: "inherit" };
		}));

		if (!this.editUserPermissionsDialog) {
			this.editUserPermissionsDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.EditUserPermissionsDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.editUserPermissionsDialog);
		}
		this.editUserPermissionsDialog.open();
	}

	public onCancelPermissions(): void {
		this.editUserPermissionsDialog?.close();
	}

	/**
	 * Mapowanie state → index w <c>RadioButtonGroup</c> (3 kolumny: inherit / grant / deny).
	 * Format binding-side; przeciwna konwersja w <see cref="onPermissionStateChange"/>.
	 */
	public formatPermissionStateIndex(state: string | null | undefined): number {
		switch (state) {
			case "grant": return 1;
			case "deny": return 2;
			default: return 0; // inherit
		}
	}

	/**
	 * Handler <c>RadioButtonGroup.select</c> — zapisuje wybrany state do row'a w view modelu.
	 * Selectedindex jest dwukierunkowy, ale formatter zwraca number bez setter'a, więc
	 * mutację trzeba zrobić tu jawnie.
	 */
	public onPermissionStateChange(event: {
		getSource(): { getBindingContext(name: string): { getProperty(name: string): unknown; getPath(): string } | null; getSelectedIndex(): number };
	}): void {
		const source = event.getSource();
		const ctx = source.getBindingContext("view");
		if (!ctx) {
			return;
		}
		const idx = source.getSelectedIndex();
		const newState: PermissionRow["state"] = idx === 1 ? "grant" : idx === 2 ? "deny" : "inherit";
		const view = this.getView().getModel("view") as JSONModel;
		view.setProperty(`${ctx.getPath()}/state`, newState);
	}

	public async onSavePermissions(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const view = this.getView().getModel("view") as JSONModel;
		const userId = view.getProperty("/editingPermissionsUserId") as string;
		const orgId = view.getProperty("/orgId") as string;
		const rows = (view.getProperty("/permissionRows") as PermissionRow[]) ?? [];

		// Zbieramy tylko non-inherit jako "+code" / "-code".
		const overrides = rows
			.filter(r => r.state !== "inherit")
			.map(r => (r.state === "grant" ? `+${r.code}` : `-${r.code}`));

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/users/${userId}/permission-overrides`, {
				method: "PUT",
				body: JSON.stringify({ overrides })
			});
			MessageToast.show(bundle.getText("organization.detail.users.permissions.dialog.success") ?? "Override'y zapisane");
			this.editUserPermissionsDialog?.close();
			await this.loadUsers(orgId);
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("organization.detail.users.permissions.dialog.failure") ?? "Nie udało się zapisać override'ów"));
		}
	}

}
